/**
 * =============================================================================
 * Player Dashboard - Final Premium Integration
 * =============================================================================
 * 
 * @description
 * High-energy player-facing dashboard with real API integration:
 * - Wallet display with crypto + fiat balances (real API)
 * - Game Lobby with category tabs and search
 * - Live Activity Feed from GET /api/v1/casino/live-activity
 * - VIP Status from GET /api/v1/casino/vip-status
 * - Profile from GET /api/v1/users/profile with PATCH update
 * - Recently Earned Rewards from bonus_rewards history
 * - Mobile-optimized with sticky bottom nav
 * 
 * @module views/player/Dashboard
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Input,
  Modal,
  Table
} from '@/components/ui';
import { PlayerNav, PlayerHomeIcon, PlayerWalletIcon, BettingIcon, HistoryIcon, SearchIcon, GiftIcon, TrophyIcon, SparklesIcon, EditIcon } from '@/components/navigation';
import { cn } from '@/lib/utils';
import { apiClient, ApiError } from '@/lib/api-client';
import {
  TokenType,
  TransactionCategory,
  TransactionStatus
} from '../../../../shared/backend/types';

// =============================================================================
// Constants
// =============================================================================

const TOKEN_LABELS: Record<TokenType, string> = {
  [TokenType.ETH]: 'ETH',
  [TokenType.USDC]: 'USDC',
  [TokenType.USDT]: 'USDT',
};

/**
 * Game categories
 */
type GameCategory = 'slots' | 'live' | 'sports';

const GAME_CATEGORIES: { id: GameCategory; label: string; icon: React.ReactNode }[] = [
  { id: 'slots', label: 'Slots', icon: <SparklesIcon /> },
  { id: 'live', label: 'Live Casino', icon: <TrophyIcon /> },
  { id: 'sports', label: 'Sports', icon: <BettingIcon /> },
];

/**
 * Mock games data (will be replaced with real API when games endpoint is available)
 */
const MOCK_GAMES = [
  { id: '1', name: 'Neon Fortune', category: 'slots', provider: 'Pragmatic Play', image: 'neon-fortune', popular: true },
  { id: '2', name: 'Sweet Bonanza', category: 'slots', provider: 'Pragmatic Play', image: 'sweet-bonanza', popular: true },
  { id: '3', name: 'Gates of Olympus', category: 'slots', provider: 'Pragmatic Play', image: 'olympus', popular: false },
  { id: '4', name: 'Live Roulette', category: 'live', provider: 'Evolution', image: 'roulette', popular: true },
  { id: '5', name: 'Lightning Blackjack', category: 'live', provider: 'Evolution', image: 'blackjack', popular: true },
  { id: '6', name: 'Live Baccarat', category: 'live', provider: 'Pragmatic Play', image: 'baccarat', popular: false },
  { id: '7', name: 'Football Studio', category: 'sports', provider: 'Evolution', image: 'football', popular: false },
  { id: '8', name: 'Mega Ball', category: 'sports', provider: 'Evolution', image: 'megaball', popular: true },
];

// =============================================================================
// Types
// =============================================================================

interface WalletData {
  user_id: string;
  cash_balance_cents: number;
  crypto_balances: { token_type: TokenType; balance: number }[];
}

interface TransactionDisplay {
  id: string;
  category: TransactionCategory;
  amount: number;
  token_type: TokenType | null;
  direction: 'credit' | 'debit';
  status: TransactionStatus;
  created_at: string;
  description: string;
}

interface VIPData {
  level: number;
  name: string;
  progress: number;
  points: number;
  nextLevelPoints: number;
  benefits: Record<string, unknown>;
}

interface ProfileData {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  vip_level: number;
  experience_points: number;
}

interface LiveActivityEvent {
  id: string;
  tenant_id?: string;
  event_type: 'big_win' | 'high_stakes_bet' | 'vip_milestone' | 'jackpot_hit' | 'large_deposit' | 'first_win';
  user_display_name: string;
  user_id: string;
  event_data_json: string | Record<string, unknown>;
  excitement_score: number;
  created_at: string;
}

interface RewardHistory {
  id: string;
  reward_type: string;
  xp_awarded: number | null;
  description: string;
  created_at: string;
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatCents(cents: number | undefined | null, tokenType?: TokenType): string {
  if (cents == null) return '—';
  if (tokenType && tokenType !== 'ETH') {
    return `$${(cents / 100).toLocaleString()}`;
  }
  return cents.toLocaleString();
}

function formatTimestamp(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return isoString;
  }
}

function getStatusBadgeClass(status: TransactionStatus): string {
  switch (status) {
    case TransactionStatus.COMPLETED: return 'badge-success';
    case TransactionStatus.PENDING:
    case TransactionStatus.PROCESSING: return 'badge-warning';
    case TransactionStatus.MANUAL_REVIEW: return 'badge-info';
    case TransactionStatus.FAILED:
    case TransactionStatus.CANCELLED: return 'badge-error';
    default: return 'badge-info';
  }
}

function getTransactionDisplay(category: TransactionCategory, direction: string): string {
  if (direction === 'credit') {
    switch (category) {
      case TransactionCategory.DEPOSIT: return 'Deposit';
      case TransactionCategory.BET: return 'Bet Win';
      case TransactionCategory.BONUS: return 'Bonus';
      case TransactionCategory.TRANSFER: return 'Cash In';
      default: return category;
    }
  } else {
    switch (category) {
      case TransactionCategory.WITHDRAWAL: return 'Withdrawal';
      case TransactionCategory.BET: return 'Bet Placed';
      case TransactionCategory.FEE: return 'Fee';
      case TransactionCategory.TRANSFER: return 'Cash Out';
      default: return category;
    }
  }
}

function parseEventData(eventDataJson: string | Record<string, unknown>): { amount?: number; currency?: string; game?: string } {
  if (typeof eventDataJson === 'string') {
    try {
      return JSON.parse(eventDataJson);
    } catch {
      return {};
    }
  }
  return eventDataJson as { amount?: number; currency?: string; game?: string };
}

// =============================================================================
// Components
// =============================================================================

function GameCard({ game, onClick }: { game: typeof MOCK_GAMES[0]; onClick: () => void }) {
  return (
    <div 
      className="game-card group" 
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="relative aspect-[4/3] bg-gradient-to-br from-background-tertiary to-background overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-xl bg-neon-cyan/20 flex items-center justify-center">
            <SparklesIcon />
          </div>
        </div>
        {game.popular && (
          <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-gold/20 text-gold text-xs font-medium">
            🔥 Popular
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      <div className="p-4 space-y-2">
        <h3 className="font-semibold text-foreground group-hover:text-neon-cyan transition-colors">
          {game.name}
        </h3>
        <div className="flex items-center justify-between">
          <span className="text-sm text-foreground-muted">{game.provider}</span>
          <span className="text-xs text-neon-cyan opacity-0 group-hover:opacity-100 transition-opacity">
            Play →
          </span>
        </div>
      </div>
    </div>
  );
}

function VIPProgress({ vip }: { vip: VIPData }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-gold-light flex items-center justify-center">
            <TrophyIcon />
          </div>
          <div>
            <p className="text-sm font-medium text-gold">{vip.name}</p>
            <p className="text-xs text-foreground-muted">Level {vip.level}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-foreground">{vip.points.toLocaleString()} XP</p>
          <p className="text-xs text-foreground-muted">{vip.nextLevelPoints - vip.points} to next</p>
        </div>
      </div>
      <div className="vip-progress">
        <div 
          className="vip-progress-bar" 
          style={{ width: `${vip.progress}%` }}
        />
      </div>
      {vip.benefits && Object.keys(vip.benefits).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {Object.entries(vip.benefits).slice(0, 3).map(([key, value]) => (
            <span key={key} className="text-xs px-2 py-1 rounded-full bg-gold/10 text-gold border border-gold/20">
              {String(value)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function RewardCard({ reward }: { reward: RewardHistory }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-background-tertiary/50 border border-border/50 animate-fade-up">
      <div className={cn(
        'w-10 h-10 rounded-lg flex items-center justify-center',
        reward.reward_type === 'level_up' ? 'bg-gold/20' : 'bg-neon-cyan/20'
      )}>
        <GiftIcon />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{reward.description}</p>
        <div className="flex items-center gap-2">
          {reward.xp_awarded && reward.xp_awarded > 0 && (
            <span className="text-xs text-neon-cyan">+{reward.xp_awarded} XP</span>
          )}
          <span className="text-xs text-foreground-subtle">{formatTimestamp(reward.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

function LiveFeedItem({ event }: { event: LiveActivityEvent }) {
  const eventData = parseEventData(event.event_data_json);
  const amount = eventData.amount;
  const currency = eventData.currency || 'USD';

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-background-tertiary/50 border border-border/50 animate-fade-up">
      <div className="live-indicator" />
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="text-neon-cyan font-medium">{event.user_display_name}</span>
          <span className="text-foreground-muted"> won </span>
          {amount && (
            <span className="text-neon-green font-medium">
              {currency === 'BTC' ? `₿${amount.toFixed(4)}` : `$${amount.toLocaleString()}`}
            </span>
          )}
        </p>
        {eventData.game && <p className="text-xs text-foreground-muted">on {eventData.game}</p>}
      </div>
      <span className="text-xs text-foreground-subtle">{formatTimestamp(event.created_at)}</span>
    </div>
  );
}

// =============================================================================
// Main Dashboard Component
// =============================================================================

export function PlayerDashboard(): React.ReactElement {
  // ---------------------------------------------------------------------------
  // State: Data
  // ---------------------------------------------------------------------------
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [transactions, setTransactions] = useState<TransactionDisplay[]>([]);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [vipData, setVipData] = useState<VIPData | null>(null);
  const [liveActivity, setLiveActivity] = useState<LiveActivityEvent[]>([]);
  const [rewards, setRewards] = useState<RewardHistory[]>([]);

  // ---------------------------------------------------------------------------
  // State: Loading
  // ---------------------------------------------------------------------------
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingVip, setLoadingVip] = useState(true);
  const [loadingLiveActivity, setLoadingLiveActivity] = useState(true);
  const [loadingRewards, setLoadingRewards] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // State: UI
  // ---------------------------------------------------------------------------
  const [activeCategory, setActiveCategory] = useState<GameCategory>('slots');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Form states
  const [depositToken, setDepositToken] = useState<TokenType>(TokenType.USDT);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawToken, setWithdrawToken] = useState<TokenType>(TokenType.USDT);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Profile edit state
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editAvatarUrl, setEditAvatarUrl] = useState('');

  // TODO: Get actual tenant ID and user ID from auth context
  const TENANT_ID = 'tenant-123';
  const USER_ID = 'user-123';

  // ---------------------------------------------------------------------------
  // API Functions
  // ---------------------------------------------------------------------------

  const fetchWallet = useCallback(async (): Promise<void> => {
    try {
      setLoadingWallet(true);
      setError(null);

      const response = await apiClient.getBalance(USER_ID);

      if (response.success && response.data) {
        setWallet(response.data);
      } else {
        throw new Error(response.error?.message || 'Failed to fetch wallet');
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load wallet.';
      setError(message);
      setWallet({ user_id: USER_ID, cash_balance_cents: 0, crypto_balances: [] });
    } finally {
      setLoadingWallet(false);
    }
  }, []);

  const fetchTransactions = useCallback(async (): Promise<void> => {
    try {
      setLoadingTransactions(true);

      const response = await apiClient.getTransactions({ page: 1, page_size: 20 });

      if (response.success && Array.isArray(response.data)) {
        const displayTransactions: TransactionDisplay[] = response.data.map(tx => ({
          id: tx.id,
          category: tx.category,
          amount: tx.amount_crypto ?? tx.amount_cents ?? 0,
          token_type: tx.token_type,
          direction: tx.direction,
          status: tx.status,
          created_at: tx.created_at,
          description: getTransactionDisplay(tx.category, tx.direction),
        }));
        setTransactions(displayTransactions);
      }
    } catch {
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, []);

  const fetchProfile = useCallback(async (): Promise<void> => {
    try {
      setLoadingProfile(true);

      const response = await apiClient.getProfile();

      if (response.success && response.data) {
        setProfile(response.data as unknown as ProfileData);
        setEditDisplayName((response.data as unknown as ProfileData).display_name || '');
        setEditAvatarUrl((response.data as unknown as ProfileData).avatar_url || '');
      }
    } catch {
      // Silent fail - profile is optional
      setProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  const fetchVipStatus = useCallback(async (): Promise<void> => {
    try {
      setLoadingVip(true);

      const response = await apiClient.getVipStatus();

      if (response.success && response.data) {
        const vip = response.data;
        setVipData({
          level: vip.current_level,
          name: vip.current_title,
          progress: vip.xp_progress_percent,
          points: vip.experience_points,
          nextLevelPoints: vip.next_level_xp,
          benefits: vip.benefits || {},
        });
      }
    } catch {
      setVipData(null);
    } finally {
      setLoadingVip(false);
    }
  }, []);

  const fetchLiveActivity = useCallback(async (): Promise<void> => {
    try {
      setLoadingLiveActivity(true);

      const response = await apiClient.getLiveActivity(TENANT_ID, { page: 1, page_size: 10 });

      if (response.success && response.data?.events) {
        setLiveActivity(response.data.events);
      }
    } catch {
      setLiveActivity([]);
    } finally {
      setLoadingLiveActivity(false);
    }
  }, []);

  const fetchRewards = useCallback(async (): Promise<void> => {
    try {
      setLoadingRewards(true);

      const response = await apiClient.getBonusRewards({ page: 1, page_size: 10 });

      if (response.success && Array.isArray(response.data)) {
        setRewards(response.data as unknown as RewardHistory[]);
      }
    } catch {
      setRewards([]);
    } finally {
      setLoadingRewards(false);
    }
  }, []);

  const updateProfile = useCallback(async (): Promise<void> => {
    if (!editDisplayName.trim()) {
      setFormError('Display name is required');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const response = await apiClient.updateProfile({
        display_name: editDisplayName.trim(),
        avatar_url: editAvatarUrl.trim() || undefined,
      });

      if (response.success && response.data) {
        setProfile(response.data as unknown as ProfileData);
        setIsProfileModalOpen(false);
      } else {
        throw new Error(response.error?.message || 'Failed to update profile');
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update profile.';
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [editDisplayName, editAvatarUrl]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  useEffect(() => {
    fetchWallet();
    fetchTransactions();
    fetchProfile();
    fetchVipStatus();
    fetchLiveActivity();
    fetchRewards();
  }, [fetchWallet, fetchTransactions, fetchProfile, fetchVipStatus, fetchLiveActivity, fetchRewards]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleDeposit = useCallback(async (): Promise<void> => {
    if (!depositAmount || Number(depositAmount) <= 0) {
      setFormError('Please enter a valid amount');
      return;
    }
    setIsSubmitting(true);
    setFormError(null);
    try {
      setIsDepositModalOpen(false);
      setDepositAmount('');
      fetchWallet();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to process deposit.';
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [depositAmount, fetchWallet]);

  const handleWithdraw = useCallback(async (): Promise<void> => {
    if (!withdrawAmount || Number(withdrawAmount) <= 0) {
      setFormError('Please enter a valid amount');
      return;
    }
    if (!withdrawAddress) {
      setFormError('Please enter a destination address');
      return;
    }
    setIsSubmitting(true);
    setFormError(null);
    try {
      const response = await apiClient.requestWithdrawal({
        token_type: withdrawToken,
        amount: withdrawAmount,
        destination_address: withdrawAddress,
      });
      if (response.success) {
        setIsWithdrawModalOpen(false);
        setWithdrawAmount('');
        setWithdrawAddress('');
        fetchTransactions();
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to process withdrawal.';
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [withdrawToken, withdrawAmount, withdrawAddress, fetchTransactions]);

  // ---------------------------------------------------------------------------
  // Filtered Games
  // ---------------------------------------------------------------------------

  const filteredGames = MOCK_GAMES.filter(game => {
    const matchesCategory = game.category === activeCategory;
    const matchesSearch = game.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          game.provider.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // ---------------------------------------------------------------------------
  // Table Columns
  // ---------------------------------------------------------------------------

  const transactionColumns = [
    { key: 'created_at', header: 'Time', width: '20%', render: (tx: TransactionDisplay) => formatTimestamp(tx.created_at) },
    { key: 'description', header: 'Type', width: '25%', render: (tx: TransactionDisplay) => (
      <span className={tx.direction === 'credit' ? 'text-neon-green' : 'text-foreground'}>
        {tx.description}
      </span>
    )},
    { key: 'token_type', header: 'Token', width: '15%', render: (tx: TransactionDisplay) => tx.token_type ? TOKEN_LABELS[tx.token_type] : 'USD' },
    { key: 'amount', header: 'Amount', width: '20%', render: (tx: TransactionDisplay) => {
      const prefix = tx.direction === 'credit' ? '+' : '-';
      return (
        <span className={tx.direction === 'credit' ? 'text-neon-green' : 'text-foreground'}>
          {prefix}{formatCents(tx.amount, tx.token_type ?? undefined)}
        </span>
      );
    }},
    { key: 'status', header: 'Status', width: '20%', render: (tx: TransactionDisplay) => (
      <span className={getStatusBadgeClass(tx.status)}>{tx.status}</span>
    )},
  ];

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const navSections = [
    { label: 'Home', to: '/player', icon: <PlayerHomeIcon /> },
    { label: 'Betting', to: '/player/betting', icon: <BettingIcon /> },
    { label: 'History', to: '/player/history', icon: <HistoryIcon /> },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col min-h-screen bg-background pb-20 md:pb-0">
      {/* Top Navigation */}
      <PlayerNav
        items={navSections}
        playerName={profile?.display_name || profile?.username || 'Player'}
        balance={{ 
          amount: formatCents(wallet?.cash_balance_cents) ?? '—', 
          currency: 'USD' 
        }}
      />

      <main className="flex-1 p-4 md:p-6 space-y-6">
        {/* Error Banner */}
        {error && (
          <div className="p-4 rounded-xl bg-error/20 border border-error/50 text-error animate-fade-up">
            <div className="flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-sm hover:underline">Dismiss</button>
            </div>
          </div>
        )}

        {/* VIP Progress Bar */}
        <Card className="card-premium animate-fade-up">
          {loadingVip ? (
            <div className="animate-pulse space-y-3">
              <div className="h-10 bg-background-tertiary rounded-lg" />
              <div className="h-3 bg-background-tertiary rounded-full" />
            </div>
          ) : vipData ? (
            <VIPProgress vip={vipData} />
          ) : (
            <p className="text-foreground-muted text-sm">VIP status unavailable</p>
          )}
        </Card>

        {/* Wallet Overview */}
        <section className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {/* Cash Wallet */}
          <Card className="wallet-card-cash animate-fade-up delay-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gold/30 flex items-center justify-center">
                <PlayerWalletIcon />
              </div>
              <span className="text-sm text-foreground-muted">Cash</span>
            </div>
            <p className="text-2xl md:text-3xl font-display font-bold text-gold">
              {loadingWallet ? '—' : formatCents(wallet?.cash_balance_cents)}
            </p>
          </Card>

          {/* Crypto Wallets */}
          {wallet?.crypto_balances?.slice(0, 2).map((crypto, idx) => (
            <Card key={crypto.token_type} className={cn('wallet-card-crypto animate-fade-up', `delay-${(idx + 2) * 100}`)}>
              <div className="flex items-center gap-2 mb-3">
                <div className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center',
                  crypto.token_type === TokenType.ETH ? 'bg-neon-cyan/30' : 'bg-neon-green/30'
                )}>
                  <PlayerWalletIcon />
                </div>
                <span className="text-sm text-foreground-muted">{TOKEN_LABELS[crypto.token_type]}</span>
              </div>
              <p className={cn(
                'text-2xl md:text-3xl font-display font-bold',
                crypto.token_type === TokenType.ETH ? 'text-neon-cyan' : 'text-neon-green'
              )}>
                {loadingWallet ? '—' : formatCents(crypto.balance, crypto.token_type)}
              </p>
            </Card>
          ))}

          {/* Deposit CTA */}
          <Card className="wallet-card-crypto animate-fade-up delay-300 flex items-center justify-center cursor-pointer hover:scale-[1.02] transition-transform"
                onClick={() => setIsDepositModalOpen(true)}>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-neon-cyan/20 flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl">+</span>
              </div>
              <p className="text-sm font-medium text-neon-cyan">Deposit</p>
            </div>
          </Card>
        </section>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Lobby - 2/3 width */}
          <div className="lg:col-span-2 space-y-4 animate-fade-up delay-200">
            <Card padding="none" className="overflow-hidden">
              <div className="p-4 md:p-6 space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <h2 className="text-xl font-display font-bold text-foreground">Game Lobby</h2>
                  
                  {/* Search */}
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-subtle">
                      <SearchIcon />
                    </span>
                    <input
                      type="text"
                      placeholder="Search games..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input pl-11"
                    />
                  </div>
                </div>

                {/* Category Tabs */}
                <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-2">
                  {GAME_CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={cn('tab-item flex items-center gap-2', activeCategory === cat.id && 'active')}
                    >
                      {cat.icon}
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Game Grid */}
              <div className="p-4 md:p-6 pt-0 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredGames.map((game, idx) => (
                  <div key={game.id} className={cn('animate-fade-up')} style={{ animationDelay: `${idx * 50}ms` }}>
                    <GameCard game={game} onClick={() => console.log('Play game:', game.name)} />
                  </div>
                ))}
              </div>

              {filteredGames.length === 0 && (
                <div className="p-8 text-center">
                  <p className="text-foreground-muted">No games found matching your search.</p>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-4 animate-fade-up delay-300">
            {/* Profile Card */}
            <Card>
              <CardHeader className="p-4 pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">My Profile</CardTitle>
                  <button 
                    onClick={() => {
                      setEditDisplayName(profile?.display_name || '');
                      setEditAvatarUrl(profile?.avatar_url || '');
                      setIsProfileModalOpen(true);
                    }}
                    className="p-1.5 rounded-lg hover:bg-background-tertiary transition-colors"
                  >
                    <EditIcon />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {loadingProfile ? (
                  <div className="flex items-center gap-3 animate-pulse">
                    <div className="w-12 h-12 rounded-full bg-background-tertiary" />
                    <div className="space-y-2">
                      <div className="h-4 w-24 bg-background-tertiary rounded" />
                      <div className="h-3 w-16 bg-background-tertiary rounded" />
                    </div>
                  </div>
                ) : profile ? (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neon-cyan to-neon-magenta flex items-center justify-center text-lg font-bold text-background">
                      {(profile.display_name || profile.username).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{profile.display_name || profile.username}</p>
                      <p className="text-sm text-foreground-muted">Level {profile.vip_level}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-foreground-muted">Profile unavailable</p>
                )}
              </CardContent>
            </Card>

            {/* Recently Earned Rewards */}
            <Card>
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-lg">Recently Earned</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {loadingRewards ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-16 bg-background-tertiary rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : rewards.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto hide-scrollbar">
                    {rewards.map(reward => (
                      <RewardCard key={reward.id} reward={reward} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-muted text-center py-4">No rewards yet. Keep playing!</p>
                )}
              </CardContent>
            </Card>

            {/* Live Activity Feed */}
            <Card>
              <CardHeader className="p-4 pb-3">
                <div className="flex items-center gap-2">
                  <div className="live-indicator" />
                  <CardTitle className="text-lg">Live Wins</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {loadingLiveActivity ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-16 bg-background-tertiary rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : liveActivity.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto hide-scrollbar">
                    {liveActivity.map(event => (
                      <LiveFeedItem key={event.id} event={event} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-muted text-center py-4">No recent big wins</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Transactions */}
        <Card padding="none" className="animate-fade-up delay-400">
          <CardHeader className="p-4 md:p-6 pb-4">
            <CardTitle>Recent Transactions</CardTitle>
          </CardHeader>
          <Table
            data={transactions}
            columns={transactionColumns}
            getKey={(tx) => tx.id}
            loading={loadingTransactions}
            emptyMessage="No transactions yet. Make your first deposit!"
          />
        </Card>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="bottom-nav">
        {navSections.map(section => (
          <button key={section.label} className="bottom-nav-item">
            <span className="nav-icon">{section.icon}</span>
            <span className="text-xs">{section.label}</span>
          </button>
        ))}
        <button 
          className="bottom-nav-item"
          onClick={() => setIsDepositModalOpen(true)}
        >
          <span className="nav-icon"><PlayerWalletIcon /></span>
          <span className="text-xs">Deposit</span>
        </button>
      </nav>

      {/* Deposit Modal */}
      <Modal
        isOpen={isDepositModalOpen}
        onClose={() => { setIsDepositModalOpen(false); setDepositAmount(''); setFormError(null); }}
        title="Deposit Crypto"
        description="Select token and amount to deposit"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsDepositModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleDeposit} disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Generate Address'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground-muted">Token</label>
            <select
              className="w-full px-3 py-2 rounded-lg bg-background-tertiary border border-border text-foreground"
              value={depositToken}
              onChange={(e) => setDepositToken(e.target.value as TokenType)}
              disabled={isSubmitting}
            >
              <option value={TokenType.ETH}>ETH</option>
              <option value={TokenType.USDC}>USDC</option>
              <option value={TokenType.USDT}>USDT</option>
            </select>
          </div>
          <Input
            label="Amount"
            placeholder="100.00"
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            disabled={isSubmitting}
          />
          {formError && <p className="text-sm text-error">{formError}</p>}
          <div className="p-4 rounded-lg bg-background-tertiary border border-border">
            <p className="text-sm text-foreground-muted">
              A deposit address will be generated after clicking "Generate Address"
            </p>
          </div>
        </div>
      </Modal>

      {/* Withdraw Modal */}
      <Modal
        isOpen={isWithdrawModalOpen}
        onClose={() => { setIsWithdrawModalOpen(false); setWithdrawAmount(''); setWithdrawAddress(''); setFormError(null); }}
        title="Withdraw Crypto"
        description="Send funds to your external wallet"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsWithdrawModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleWithdraw} disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Withdraw'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground-muted">Token</label>
            <select
              className="w-full px-3 py-2 rounded-lg bg-background-tertiary border border-border text-foreground"
              value={withdrawToken}
              onChange={(e) => setWithdrawToken(e.target.value as TokenType)}
              disabled={isSubmitting}
            >
              <option value={TokenType.ETH}>ETH</option>
              <option value={TokenType.USDC}>USDC</option>
              <option value={TokenType.USDT}>USDT</option>
            </select>
          </div>
          <Input
            label="Amount"
            placeholder="100.00"
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            disabled={isSubmitting}
          />
          <Input
            label="Destination Address"
            placeholder="0x..."
            value={withdrawAddress}
            onChange={(e) => setWithdrawAddress(e.target.value)}
            error={formError && !withdrawAddress ? 'Address is required' : undefined}
            disabled={isSubmitting}
          />
          {formError && <p className="text-sm text-error">{formError}</p>}
        </div>
      </Modal>

      {/* Profile Edit Modal */}
      <Modal
        isOpen={isProfileModalOpen}
        onClose={() => { setIsProfileModalOpen(false); setFormError(null); }}
        title="Edit Profile"
        description="Update your display name and avatar"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsProfileModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={updateProfile} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Display Name"
            placeholder="Your display name"
            value={editDisplayName}
            onChange={(e) => setEditDisplayName(e.target.value)}
            error={formError && !editDisplayName.trim() ? 'Display name is required' : undefined}
            disabled={isSubmitting}
          />
          <Input
            label="Avatar URL (optional)"
            placeholder="https://example.com/avatar.jpg"
            value={editAvatarUrl}
            onChange={(e) => setEditAvatarUrl(e.target.value)}
            disabled={isSubmitting}
          />
          {formError && <p className="text-sm text-error">{formError}</p>}
        </div>
      </Modal>
    </div>
  );
}